// A versão corrente do delegation log (Parte 5.2/5.7; REQ-AUTHZ-006): pessoa × função × tarefas × vigência × situação, com o motivo da situação (suspensão, revogação, expiração)
// e as assinaturas do PI e do aceite. As ações aparecem só com a permissão e quando cabem na situação — o servidor continua sendo a autoridade: assinar e revogar
// (`delegation.sign`, só o PI registrado), emendar (`delegation.manage`, só a ativa e sem outra versão pendente).
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@giyus/ui';
import { useTranslation } from 'react-i18next';

import { useDateFormatter } from '../../../../lib/dates/useDateFormatter';
import { useCivilDate } from '../../../people/lib/civilDateDisplay';
import type { Delegation, StudyRef } from '../../api/delegations';
import {
  canAmendDelegation,
  canRevokeDelegation,
  canSignDelegation,
  shortId,
  statusReasonKey,
} from '../delegationModel';
import { useFunctionName, useTaskName } from '../delegationNames';
import { DelegationStatusBadge } from './DelegationStatusBadge';
import { SignDelegationButton } from './SignDelegationButton';

export interface DelegationLogTableProps {
  readonly study: StudyRef;
  /** As delegações vivas (não terminais) a mostrar. */
  readonly delegations: readonly Delegation[];
  /** O histórico completo (decide se uma ativa pode ser emendada). */
  readonly all: readonly Delegation[];
  readonly canManage: boolean;
  readonly canSign: boolean;
  readonly taskNames?: ReadonlyMap<string, string> | undefined;
  readonly onAmend: (delegation: Delegation) => void;
  readonly onRevoke: (delegation: Delegation) => void;
  readonly onDone: (message: string) => void;
}

export function DelegationReasonText({ reason }: { readonly reason: string | null }) {
  const { t } = useTranslation('authz');
  if (reason === null) {
    return null;
  }
  const key = statusReasonKey(reason);
  return (
    <span
      className="block text-caption text-muted-foreground"
      data-testid="delegation-status-reason"
    >
      {t('delegations.log.reason')}: {key ? t(key, { defaultValue: reason }) : reason}
    </span>
  );
}

export function DelegationSignatures({ delegation }: { readonly delegation: Delegation }) {
  const { t } = useTranslation('authz');
  const dates = useDateFormatter();
  return (
    <ul className="flex flex-col text-caption">
      <li data-testid="delegation-pi-signature">
        {delegation.piSignature
          ? t('delegations.log.piSigned', { when: dates.dateTime(delegation.piSignature.signedAt) })
          : t('delegations.log.piPending')}
      </li>
      <li data-testid="delegation-acceptance">
        {delegation.acceptance
          ? t('delegations.log.accepted', { when: dates.dateTime(delegation.acceptance.signedAt) })
          : t('delegations.log.acceptancePending')}
      </li>
    </ul>
  );
}

export function DelegationLogTable({
  study,
  delegations,
  all,
  canManage,
  canSign,
  taskNames,
  onAmend,
  onRevoke,
  onDone,
}: DelegationLogTableProps) {
  const { t } = useTranslation('authz');
  const civil = useCivilDate();
  const taskName = useTaskName();
  const functionName = useFunctionName();
  return (
    <Table data-testid="delegation-log-table" regionLabel={t('delegations.log.current.title')}>
      <TableHeader>
        <TableRow>
          <TableHead>{t('delegations.log.columns.person')}</TableHead>
          <TableHead>{t('delegations.log.columns.function')}</TableHead>
          <TableHead>{t('delegations.log.columns.tasks')}</TableHead>
          <TableHead>{t('delegations.log.columns.validity')}</TableHead>
          <TableHead>{t('delegations.log.columns.status')}</TableHead>
          <TableHead>{t('delegations.log.columns.signatures')}</TableHead>
          <TableHead>
            <span className="sr-only">{t('delegations.log.columns.actions')}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {delegations.map((delegation) => (
          <TableRow key={delegation.id} data-testid={`delegation-row-${delegation.id}`}>
            <TableCell>
              <span className="font-medium">
                {delegation.personDisplayName ?? `…${shortId(delegation.personId)}`}
              </span>
              <span className="block text-caption text-muted-foreground">
                {t('delegations.log.version', { version: delegation.version })}
              </span>
            </TableCell>
            <TableCell>{functionName(delegation.functionCode)}</TableCell>
            <TableCell>
              <ul className="flex flex-col text-caption">
                {delegation.tasks.map((task) => (
                  <li key={task.taskCode}>
                    {taskName(task.taskCode, task.name ?? taskNames?.get(task.taskCode))}
                  </li>
                ))}
              </ul>
            </TableCell>
            <TableCell>
              {t('delegations.validity', {
                from: civil(delegation.effectiveFrom),
                to: delegation.effectiveTo
                  ? civil(delegation.effectiveTo)
                  : t('delegations.openEnded'),
              })}
            </TableCell>
            <TableCell>
              <DelegationStatusBadge
                delegation={delegation}
                testId={`delegation-status-${delegation.id}`}
              />
              <DelegationReasonText reason={delegation.statusReason} />
            </TableCell>
            <TableCell>
              <DelegationSignatures delegation={delegation} />
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-2">
                {canSign && canSignDelegation(delegation) && (
                  <SignDelegationButton
                    study={study}
                    delegation={delegation}
                    taskNames={taskNames}
                    onDone={onDone}
                  />
                )}
                {canManage && canAmendDelegation(delegation, all) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`delegation-amend-${delegation.id}`}
                    onClick={() => {
                      onAmend(delegation);
                    }}
                  >
                    {t('delegations.actions.amend')}
                  </Button>
                )}
                {canSign && canRevokeDelegation(delegation) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`delegation-revoke-${delegation.id}`}
                    onClick={() => {
                      onRevoke(delegation);
                    }}
                  >
                    {t('delegations.actions.revoke')}
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
